/**
 * A tiny, dependency-free line-level diff (LCS backtrace) for the Proposals
 * before/after view. The rich docz-aware diff (`web-codeblock` over `diffz` per
 * component-map §4) is a later track; this is the honest "diff-ish" fallback the
 * accept/reject surface needs today — deterministic, tested, no third-party dep.
 *
 * Flagged as a post-v1 extraction candidate: a generic `web-*` line-diff has no
 * elaborate-specific concern, so if a second consumer appears it should move to
 * `libs/frontend/web/` (Rule Zero, no consumer-gating). For one consumer it stays
 * app-local.
 */

export type DiffLineType = "equal" | "add" | "remove";

export interface DiffLine {
  type: DiffLineType;
  text: string;
}

function splitLines(s: string): string[] {
  if (s === "") return [];
  return s.replace(/\r\n/g, "\n").split("\n");
}

/**
 * Line diff of `before` → `after` via a longest-common-subsequence backtrace.
 * Common lines are `equal`, lines only in `before` are `remove`, lines only in
 * `after` are `add` — in reading order.
 */
export function lineDiff(before: string, after: string): DiffLine[] {
  const a = splitLines(before);
  const b = splitLines(after);
  const n = a.length;
  const m = b.length;

  // lcs[i][j] = LCS length of a[i..] and b[j..].
  const lcs: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] =
        a[i] === b[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ type: "equal", text: a[i] });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ type: "remove", text: a[i] });
      i++;
    } else {
      out.push({ type: "add", text: b[j] });
      j++;
    }
  }
  while (i < n) out.push({ type: "remove", text: a[i++] });
  while (j < m) out.push({ type: "add", text: b[j++] });
  return out;
}

/** Count added / removed lines — drives the "+N −M" summary chip. */
export function diffStats(lines: DiffLine[]): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const l of lines) {
    if (l.type === "add") added++;
    else if (l.type === "remove") removed++;
  }
  return { added, removed };
}
